import {
  Table,
  Form,
  InputGroup,
  Button,
  Modal,
  Dropdown,
} from "react-bootstrap";
import CachedIcon from "mdi-react/CachedIcon";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import DotsHorizontalIcon from "mdi-react/DotsHorizontalIcon";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import queryString from "query-string";

import "./../../assets/scss/reports/cashbook.scss";
import useDebounce, {
  useScrollTop,
  useBackendUrl,
  useEffectOnce,
  useQueryParams,
} from "../../utils/hooks";
import { useAuth } from "../../hooks/useAuth";
import NoTableItem from "../utils/NoTableItem";
import { IsPrivileged } from "../DisplayChildElement";
import { DeleteIcon, EditIcon } from "../Icons";
import ConfirmDialog from "../ConfirmDialogue";
import LoginDialog from "../LoginDialog";
import { description } from "platform";
import PageHeader from "../PageHeader";
import { formatDate } from "../../utils/helpers";

// API endpoints — adjust here if backend uses different paths.
const ENDPOINTS = {
  list: "/api/branch",
  create: "/api/branch/create",
  update: "/api/branch/update",
  remove: "/api/branch/delete",
};

const initialValues = {
  name: "",
  oldName: "",
  description: "",
};

const schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Branch name is required"),
  description: yup.string().trim(),
});

// ---------------------------------------------------------------------------
// Create Modal
// ---------------------------------------------------------------------------
function CreateBranchModal({ show, onHide, refetch }) {
  const backendUrl = useBackendUrl();

  const createBranch = async payload => {
    let response = await fetch(`${backendUrl}${ENDPOINTS.create}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const createBranchMutation = useMutation(payload => createBranch(payload), {
    onSuccess: ({ message }) => {
      toast.success(message || "Branch created");
      formik.resetForm();
      refetch();
      onHide();
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to create branch: ${message}`);
    },
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: values => {
      createBranchMutation.mutate({
        name: values.name.trim(),
        description: values.description.trim(),
      });
    },
  });

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h1 className="h5">Create Branch</h1>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              name="name"
              type="text"
              placeholder="Branch name"
              value={formik.values.name}
              onChange={formik.handleChange}
              isInvalid={formik.touched.name && !!formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              name="description"
              type="text"
              placeholder="Branch description"
              value={formik.values.description}
              onChange={formik.handleChange}
              isInvalid={
                formik.touched.description && !!formik.errors.description
              }
              as={"textarea"}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            disabled={createBranchMutation.isLoading}
            variant="primary"
            className="w-100 p-2"
            type="submit"
          >
            {createBranchMutation.isLoading ? "Please wait…" : "Create Now"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit Modal
// ---------------------------------------------------------------------------
function EditBranchModal({ show, onHide, selectedBranch, refetch }) {
  const backendUrl = useBackendUrl();

  const updateBranch = async payload => {
    let response = await fetch(`${backendUrl}${ENDPOINTS.update}`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const updateBranchMutation = useMutation(payload => updateBranch(payload), {
    onSuccess: ({ message }) => {
      toast.success(message || "Branch updated");
      formik.resetForm();
      refetch();
      onHide();
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to update branch: ${message}`);
    },
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: values => {
      updateBranchMutation.mutate({
        id: selectedBranch?.id,
        name: values.name.trim(),
        description: values.description.trim(),
        oldName: values.oldName,
      });
    },
  });

  useEffectOnce(() => {
    formik.setValues({
      name: selectedBranch?.name || "",
      oldName: selectedBranch?.name || "",
    });
  });

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h1 className="h5">Edit {formik.values.oldName}</h1>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              name="name"
              type="text"
              placeholder="Branch name"
              value={formik.values.name}
              onChange={formik.handleChange}
              isInvalid={formik.touched.name && !!formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              name="description"
              type="text"
              placeholder="Branch description"
              value={formik.values.description}
              onChange={formik.handleChange}
              isInvalid={
                formik.touched.description && !!formik.errors.description
              }
              as={"textarea"}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            disabled={updateBranchMutation.isLoading}
            variant="primary"
            className="w-100 p-2"
            type="submit"
          >
            {updateBranchMutation.isLoading ? "Please wait…" : "Save Changes"}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function BranchesPage() {
  useScrollTop();
  const { backendUrl, token } = useAuth();

  const initialFilterParams = { name: "" };
  const [queryParams, setQueryParams] = useQueryParams({
    ...initialFilterParams,
  });
  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });
  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams(prev => ({ ...prev, ...debouncedFilterParams }));
  }, [debouncedFilterParams]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // ---- List query ------------------------------------------------------
  const getBranches = async params => {
    let response = await fetch(
      `${backendUrl}${ENDPOINTS.list}?${queryString.stringify(params)}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const json = await response.json();
    // Be flexible about response shape.
    return (
      json?.data?.branches ||
      json?.branches ||
      json?.data ||
      (Array.isArray(json) ? json : [])
    );
  };

  const { data: branches = [], refetch, isFetching, isSuccess } = useQuery(
    ["GET_BRANCHES", queryParams],
    () => getBranches(queryParams),
    { keepPreviousData: true }
  );

  // ---- Delete mutation -------------------------------------------------
  const deleteBranch = async payload => {
    let response = await fetch(`${backendUrl}${ENDPOINTS.remove}`, {
      method: "DELETE",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const deleteBranchMutation = useMutation(payload => deleteBranch(payload), {
    onSuccess: ({ message }) => {
      toast.success(message || "Branch deleted");
      refetch();
    },
    onError: ({ message = "" }) => {
      toast.error(`Unable to delete branch: ${message}`);
    },
  });

  // ---- Handlers --------------------------------------------------------
  const handleFilterParamsChange = e => {
    setFilterParams({
      ...filterParams,
      [e.target.name]: e.target.value,
    });
  };

  const handleEdit = async branch => {
    if (await LoginDialog()) {
      setSelectedBranch(branch);
      setShowEditModal(true);
    }
  };

  const handleDelete = async branch => {
    if (await LoginDialog()) {
      if (
        await ConfirmDialog({
          title: "Delete Branch",
          description: `Are you sure you want to delete "${branch?.name}"?`,
          type: "danger",
        })
      ) {
        deleteBranchMutation.mutate({ id: branch?.id, name: branch?.name });
      }
    }
  };

  return (
    // <IsPrivileged roleName={["Branches"]}>
    <main className="cash-book table-sticky-header">
      <PageHeader name={`Branch Management`} description={``} />
      <div className="content pb-4 ">
        <header className="container">
          <h1>
            Branches
            <button
              title="Refresh"
              onClick={() => refetch()}
              className="btn text-primary"
              disabled={isFetching}
            >
              <CachedIcon />
            </button>
          </h1>

          <div className="actions mr-5 d-flex gap-3">
            <div>
              <InputGroup>
                <Form.Control
                  name="name"
                  placeholder="search branch..."
                  value={filterParams?.name || ""}
                  onChange={handleFilterParamsChange}
                />
                {/* <InputGroup.Text>
                  <MagnifyIcon />
                </InputGroup.Text> */}
              </InputGroup>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="px-3">
              + Create New
            </Button>
          </div>
        </header>

        <div>
          <Table borderless striped className="product-table container">
            <thead className="position-sticky top-0">
              <tr>
                <th />
                <th>S/N</th>
                <th>Branch</th>
                <th>Date Created</th>
              </tr>
            </thead>
            <tbody>
              {branches?.map((branch, i) => (
                <tr key={branch?.id ?? `${branch?.name}-${i}`}>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle
                        variant=""
                        className="bg-white border-0"
                        bsPrefix="print more"
                      >
                        <DotsHorizontalIcon />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{ strategy: "fixed" }}
                        renderOnMount
                        className="dropdown-with-icons"
                      >
                        <Dropdown.Item
                          as="button"
                          onClick={() => handleEdit(branch)}
                        >
                          <EditIcon /> Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          as="button"
                          onClick={() => handleDelete(branch)}
                        >
                          <DeleteIcon /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td>{i + 1}</td>
                  <td>
                    <p className="fw-semibold">{branch?.name}</p>{" "}
                    <small>{branch?.description}</small>
                  </td>
                  <td>{formatDate(branch.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {!isFetching && isSuccess && isEmpty(branches) && (
            <NoTableItem
              queryParams={filterParams}
              title={
                filterParams?.name
                  ? "No branches match your search"
                  : "No branches yet"
              }
            >
              {!filterParams?.name && (
                <div className="text-center mt-3">
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                    className="px-3"
                  >
                    + Create your first branch
                  </Button>
                </div>
              )}
            </NoTableItem>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateBranchModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          refetch={refetch}
        />
      )}

      {showEditModal && !isEmpty(selectedBranch) && (
        <EditBranchModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedBranch(null);
          }}
          selectedBranch={selectedBranch}
          refetch={refetch}
        />
      )}
    </main>
    // </IsPrivileged>
  );
}

export default BranchesPage;
